import { CommonModule } from '@angular/common';
import { Component, Input, inject, ChangeDetectionStrategy } from '@angular/core';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, map } from 'rxjs';

import { FeatureFlag } from 'app/core/enums/feature-flag';
import { Pipeline } from 'app/modules/projects/projects.types';
import { FeatureFlagService } from 'app/core/services/feature-flag.service';

declare type ViewTypes = 'grid' | 'kanban' | 'list' | 'map';

@Component({
  selector: 'project-list-header',
  templateUrl: './header.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterModule, MatButtonModule, MatIconModule, MatTooltipModule],
})
export class ProjectsListHeaderComponent {
  private _router = inject(Router);
  private _activatedRoute = inject(ActivatedRoute);
  private _featureFlagService = inject(FeatureFlagService);

  @Input() pipeline: Pipeline;

  get hasPipelineMapEnabled$(): Observable<boolean> {
    return this._featureFlagService.hasFeatureFlag(FeatureFlag.PIPELINE_MAP);
  }

  /**
   * Uses the queryParamMap observable to get the current query parameters.
   * Maps the queryParamMap to extract the filterId if it exists.
   * If filterId exists, it creates an object with the filterId; otherwise, it creates an empty object.
   * Navigate to the sibling route.
   * Passes the queryParams object, which will contain the filterId if it exists.
   * Uses queryParamsHandling: 'merge' to preserve any other existing query parameters.
   * @param path as ViewTypes
   */
  navigateTo(path: ViewTypes): void {
    this._activatedRoute.queryParamMap
      .pipe(
        map((params) => params.get('filterId')),
        map((filterId) => (filterId ? { filterId } : {})),
      )
      .subscribe((queryParams) => {
        this._router.navigate(['../', path], {
          relativeTo: this._activatedRoute,
          queryParams: queryParams,
          queryParamsHandling: 'merge',
        });
      });
  }
}
